import { isAxiosError } from "axios";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { emitApiError } from "../../api/errorBus";
import { useCreateUser, usePatchUserAdmin, useUsers } from "../../api/users";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { Field } from "../../components/ui/Field";
import { FilterBar } from "../../components/ui/FilterBar";
import { Form } from "../../components/ui/Form";
import { Modal } from "../../components/ui/Modal";
import { Page } from "../../components/ui/Page";
import { Pagination } from "../../components/ui/Pagination";
import { Spinner } from "../../components/ui/Spinner";
import { Table } from "../../components/ui/Table";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { usePaginationParams } from "../../hooks/usePaginationParams";
import { useSortParams } from "../../hooks/useSortParams";
import { useTranslation } from "../../hooks/useTranslation";
import type { CreateUserRequest, UserResponse } from "../../types/user";
import styles from "./UsersListPage.module.css";

const PAGE_SIZE = 20;

export default function UsersListPage() {
  const { t, language } = useTranslation();
  useLayoutTitle(t("users.allUsers"));
  const currentUser = useCurrentUser();
  const createUser = useCreateUser();

  const isAdmin = currentUser.isAdmin;

  const patchAdmin = usePatchUserAdmin();
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const handleToggleAdmin = (userId: string, current: boolean) => {
    setTogglingId(userId);
    patchAdmin.mutate({ id: userId, isAdmin: !current }, { onSettled: () => setTogglingId(null) });
  };

  type RoleFilter = "all" | "admin" | "nonAdmin";
  type AccessFilter = "with" | "";
  type SortKey = "lastName" | "lastAccess";

  const [searchParams, setSearchParams] = useSearchParams();
  const roleFilter = (searchParams.get("role") ?? "all") as RoleFilter;
  const search = searchParams.get("search")?.trim() || undefined;
  const setRoleFilter = (value: RoleFilter) => {
    setSearchParams((prev) => {
      if (value === "all") prev.delete("role");
      else prev.set("role", value);
      prev.set("page", "1");
      return prev;
    });
  };

  const withAccess = searchParams.get("withAccess") === "true";
  const withoutAccess = searchParams.get("withoutAccess") === "true";
  const setWithAccess = (value: AccessFilter) => {
    setSearchParams((prev) => {
      if (value === "with") prev.set("withAccess", "true");
      else prev.delete("withAccess");
      prev.set("page", "1");
      return prev;
    });
  };
  const setWithoutAccess = (value: AccessFilter) => {
    setSearchParams((prev) => {
      if (value === "with") prev.set("withoutAccess", "true");
      else prev.delete("withoutAccess");
      prev.set("page", "1");
      return prev;
    });
  };
  const access = withAccess === withoutAccess ? undefined : withAccess ? "with" : "without";

  const { sortBy, sortDir, setSort } = useSortParams<SortKey>("lastName");

  const rawPage = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10));
  const { data, isLoading } = useUsers({
    role: roleFilter === "all" ? undefined : roleFilter,
    access,
    search,
    sortBy,
    sortDir,
    page: rawPage,
    limit: PAGE_SIZE,
  });

  const [users, setUsers] = useState<UserResponse[]>([]);
  useEffect(() => {
    data?.data && setUsers(data.data);
  }, [data?.data]);
  const { page, totalPages, setPage } = usePaginationParams(PAGE_SIZE, data?.total ?? 0);

  const [addOpen, setAddOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<CreateUserRequest>();

  const openAdd = () => {
    reset();
    setSuccessMsg("");
    setAddOpen(true);
  };

  const closeAdd = () => {
    reset();
    setAddOpen(false);
  };

  const onSubmit = (data: CreateUserRequest) => {
    createUser.mutate(data, {
      onSuccess: () => {
        setSuccessMsg(t("users.addUserSuccess"));
        reset();
        setAddOpen(false);
      },
      onError: (error) => {
        if (isAxiosError(error) && error.response?.status === 404) {
          setError("email", { message: t("users.userNotFound") });
        } else {
          emitApiError(isAxiosError(error) ? error.response?.data?.error?.message ?? null : null);
        }
      },
    });
  };

  return (
    <Page>
      <Page.TitleRow>
        <Page.FilterRow>
          <FilterBar
            value={roleFilter}
            options={[
              { value: "all", label: t("users.roleAll") },
              { value: "admin", label: t("users.roleAdmin") },
              { value: "nonAdmin", label: t("users.roleNonAdmin") },
            ]}
            onChange={setRoleFilter}
          />
          <Page.FilterDivider />
          <FilterBar<AccessFilter>
            value={withAccess ? "with" : ""}
            options={[{ value: "with", label: t("users.withAccess") }]}
            onChange={setWithAccess}
            nullable
          />
          <FilterBar<AccessFilter>
            value={withoutAccess ? "with" : ""}
            options={[{ value: "with", label: t("users.withoutAccess") }]}
            onChange={setWithoutAccess}
            nullable
          />
          {isLoading && <Spinner />}
        </Page.FilterRow>
        {isAdmin && <Button onClick={openAdd}>+</Button>}
      </Page.TitleRow>

      {successMsg && <Form.Feedback type="success">{successMsg}</Form.Feedback>}

      {users.length === 0 ? (
        <EmptyState>{t("users.noUsers")}</EmptyState>
      ) : (
        <>
          <Table>
            <Table.Head>
              <Table.HeaderCell sortDir={sortBy === "lastName" ? sortDir : null} onSort={() => setSort("lastName")}>
                {t("users.name")}
              </Table.HeaderCell>
              <Table.HeaderCell>{t("users.department")}</Table.HeaderCell>
              <Table.HeaderCell>{t("users.isAdmin")}</Table.HeaderCell>
              <Table.HeaderCell sortDir={sortBy === "lastAccess" ? sortDir : null} onSort={() => setSort("lastAccess")}>
                {t("users.lastAccess")}
              </Table.HeaderCell>
            </Table.Head>
            <Table.Body>
              {users.map((user) => (
                <Table.Row key={user.id}>
                  <Table.Cell>
                    <div className={styles["userCell"]}>
                      <span className={styles["userName"]}>
                        {user.lastName} {user.firstName}
                      </span>
                      <Link to={`mailto:${user.email}`} className={styles["userEmail"]}>
                        {user.email}
                      </Link>
                    </div>
                  </Table.Cell>
                  <Table.Cell variant="muted">{user.department ?? "—"}</Table.Cell>
                  <Table.Cell>
                    {isAdmin ? (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={user.isAdmin}
                        aria-label={t("users.isAdmin")}
                        disabled={togglingId === user.id || user.id === currentUser?.id}
                        data-self={String(user.id === currentUser?.id)}
                        data-checked={String(user.isAdmin)}
                        className={styles["toggle"]}
                        onClick={() => handleToggleAdmin(user.id, user.isAdmin)}
                      >
                        <span className={styles["toggleThumb"]} />
                      </button>
                    ) : user.isAdmin ? (
                      <Badge variant="completed" label={t("users.isAdmin")} />
                    ) : (
                      <Badge variant="default" label="—" />
                    )}
                  </Table.Cell>
                  <Table.Cell variant="muted">
                    {user.lastAccess ? new Date(user.lastAccess).toLocaleString(language) : "—"}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      <Modal
        open={addOpen}
        title={t("users.addUser")}
        onClose={closeAdd}
        footer={
          <Form.Actions>
            <Button variant="secondary" onClick={closeAdd}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form="add-user-form" loading={createUser.isPending}>
              {t("users.addUser")}
            </Button>
          </Form.Actions>
        }
      >
        <Form id="add-user-form" onSubmit={handleSubmit(onSubmit)}>
          <Field.Input
            label={t("users.email")}
            autoFocus
            error={errors.email?.message}
            {...register("email", {
              required: t("common.required"),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t("common.required"),
              },
            })}
          />
          {createUser.isError &&
            !(isAxiosError(createUser.error) && createUser.error.response?.status === 404) && (
              <Form.Feedback type="error">{t("common.error")}</Form.Feedback>
            )}
        </Form>
      </Modal>
    </Page>
  );
}
